import { FunctionsHttpError } from '@supabase/supabase-js'
import { toast } from 'sonner'
import { upgradeEvents } from '@/lib/upgrade'

/** A credit refusal from an edge function: the only 402 they send. */
export function isCreditRefusal(error: unknown): boolean {
  for (let e: unknown = error; e; e = (e as { cause?: unknown }).cause) {
    if (e instanceof FunctionsHttpError && e.context.status === 402) {
      return true
    }
    if (!(e instanceof Error)) {
      break
    }
  }
  return false
}

/**
 * What a refused credit spend shows, wherever it happens: the plans dialog
 * for someone who can pay, a short explanation for everyone else. Called
 * once per refusal, from the Supabase client (see `billingAwareFetch`).
 */
export function showCreditRefusal(): void {
  if (upgradeEvents.available) {
    upgradeEvents.open({ kind: 'credits' })
    return
  }
  toast.error('Out of credits', {
    description: 'Ask an organization admin to add credits.',
  })
}

/**
 * Callers ask this before their own error toast: a credit refusal has
 * already been shown (plans dialog or toast), so they stay quiet.
 */
export async function handledCreditRefusal(error: unknown): Promise<boolean> {
  return isCreditRefusal(error)
}

const WATERMARK_HINT_KEY = 'upgrade.watermark-hint'

/**
 * Said once per session, after the first watermarked export: the file they
 * just saved carries the Lumoo logo, and a paid plan removes it.
 */
export function noteWatermarkedDownload(): void {
  // Only a self-serve admin can act on it; a member or an Enterprise POC
  // would get a hint with nothing behind it.
  if (!upgradeEvents.available) {
    return
  }
  try {
    if (sessionStorage.getItem(WATERMARK_HINT_KEY)) {
      return
    }
    sessionStorage.setItem(WATERMARK_HINT_KEY, '1')
  } catch {
    // Storage blocked: say it every time rather than never.
  }
  toast('Exported with the Lumoo watermark', {
    description: 'Paid plans export clean files.',
    action: {
      label: 'Upgrade',
      onClick: () => upgradeEvents.open({ kind: 'watermark' }),
    },
  })
}
